/**
 * The MONSTER stream's own state for one monster (spec 02 §5): the drop-in, the start delay, the
 * walk/jump state machine, attack cooldowns... Plain numbers and booleans only (06 R5).
 *
 * Each field is one of the C64's per-sprite arrays (rebb64 names in brackets), so the port reads
 * like the 6502 it came from. Values the C64 keeps as $FF are -1 here.
 */
export interface MonsterAi {
  /** I-ticks left before its AI starts (the level data's delay x 2) [D_8638]. */
  delay: number;
  /** Where its drop-in stops: the spawn row's Y [D_8890 during the drop]. */
  targetY: number;
  /** Index in the level data = hardware sprite slot - 2; its parity picks the player it chases. */
  slot: number;
  /** Sprite frame [D_8520]: frames >= `faceFrames` face left; the renderer's walk cycle. */
  frame: number;
  /** Frames per facing group [D_8750] (4 for most kinds). */
  faceFrames: number;
  /** Updates since the frame last advanced [D_8610]. */
  anim: number;
  /** The monster's "joystick" [D_85E8]: 1 left, 2 right, 4 jump (walkers) / up (fliers), 8 down. */
  joy: number;
  /** Falling (walked off a ledge, or started above the screen) [D_87F0 >= 0]. */
  falling: boolean;
  /** Jump counter [D_87A0]: -1 none; $1F..$10 crouching; $0F..$01 rising; 0 coming down. */
  jump: number;
  /** Steps of the jump's descent taken [D_87C8], -1 before the first. */
  descent: number;
  /** Gap-jump drift [D_8840 left, D_8868 right]: -1 drifting, 1 stopped by a wall, 0 none. */
  driftL: number;
  driftR: number;
  /** May gap-jump [D_86D8]: the player is level with it, or in the gap phase while above. */
  gapOk: boolean;
  /** May jump up to a platform [D_8700]: the vertical phase while the player is above. */
  vertOk: boolean;
  /** Which permission the next toggle grants: false gap, true vertical [D_86B0 = x or x+40]. */
  nextVert: boolean;
  /** I-ticks (while the player is above) to the next toggle [D_8660], and its reload [D_8688]. */
  timer: number;
  interval: number;
  /**
   * Attack state [D_8818]: -1 idle; 7..1 the wind-up of Hidegons, Drunk and Mighta (D_ED3B); 0 the
   * update the shot leaves on; Drunk waiting for its bottle counts down from $2C, then sits at 0/1
   * (L_ED37). The Invader: 100 while its laser is out (D_1E9F), -1 again when the laser ends (L12F9).
   */
  attack: number;
  /** Attack cooldown [D_8890 after the drop-in]: AI updates until it may attack again. */
  cooldown: number;
  /** A software slot is held for the shot of the wind-up under way [D_87A0 = the slot, L_EE31]. */
  reserved: boolean;
  /**
   * The power heart (D_34A0): 0 not frozen; 1 frozen and falling (state $16, D_2785); 2 frozen and
   * landed (state $17, L_27CB). `Monster.frozen` is set with it.
   */
  heart: number;
  /** Set once an escape from a bubble has been set up (the fall in state $0A, D_2808). */
  escaping: boolean;
}

export function initialMonsterAi(delay: number, targetY: number): MonsterAi {
  return {
    delay, targetY, slot: 0, frame: 0, faceFrames: 4, anim: 0, joy: 0, falling: false, jump: -1, descent: -1,
    driftL: 0, driftR: 0, gapOk: false, vertOk: false, nextVert: false, timer: 0, interval: 0, attack: -1,
    cooldown: 0, reserved: false, heart: 0, escaping: false,
  };
}

/** The MONSTER stream's own state for the Baron (02 §6): leg length, pause, direction. */
export interface BaronAi {
  /** M-ticks of pause left before the current leg [D_A775]. */
  pause: number;
  /** Software-sprite column and row [$DC, $EE]: column c is playfield column c, row r is row r - 3. */
  col: number;
  row: number;
  /** Current leg length, bit 7 set for a horizontal leg [D_AA30]. */
  leg: number;
  /** Cells left in the current leg, bit 7 set while horizontal [D_AA42]. */
  steps: number;
  /** Facing its player when it last moved [D_E765: 2 = left]. */
  faceLeft: boolean;
  /** `world.clock.mTicks` of the last main-loop pass it has run. */
  mTick: number;
}

export function initialBaronAi(col: number, row: number, mTick: number): BaronAi {
  return { pause: 0, col, row, leg: 0, steps: 0, faceLeft: false, mTick };
}

/**
 * The MONSTER stream's own state for level 100's boss (02 §9.4), after the zero-page bytes the C64
 * keeps it in on that level ($BC..$C0, the spare X registers of sprites 2..6): its way, its picture,
 * the stun timer (D_5BBF), the win's count-up. Plain numbers and booleans only.
 */
export interface BossAi {
  /** Pixels per move across and down, +/-2 [$BF: bit 0 right, bit 3 down]. Angry, it moves twice an I-tick. */
  dx: number;
  dy: number;
  /** M-ticks of the stun left (130 at 0 HP) [D_5BBF while $BE = 9], else 0. */
  stun: number;
  /** The picture set [$BE]: 0 facing left, $18 facing right (flipped at each side bounce). */
  face: number;
  /** I-ticks of play until it appears, as the READY banner goes (D_1694 on level 100). */
  wake: number;
  /** `world.clock.mTicks` of the pass the stun began in: the timer counts from the next pass. */
  stunPass: number;
  /** Who touched it while stunned [D_A5B8], or -1. */
  winner: number;
  /** I-ticks of the 1,000,000 count-up left (D_A5B7). */
  payout: number;
}

export function initialBossAi(): BossAi {
  return { dx: -2, dy: -2, stun: 0, face: 0, wake: 0, stunPass: -1, winner: -1, payout: 0 };
}
